import { useAuth0 } from '@auth0/auth0-react';
import { UtilityText } from '@policygenius/mortar';
import React from 'react';
import { useDispatch } from 'react-redux';

import { trackSelectedOption } from 'analytics/navigator';
import { StepName } from 'analytics/navigator/constants';
import Button from 'components/Button';
import MultipleChoice from 'components/MultipleChoice';
import { IMultipleChoiceOption } from 'components/MultipleChoice/types';
import { openPage, startLoader } from 'containers/App/actions';
import { createNewSpousalAppComplete } from 'containers/Basics/Info/hooks/commands';
import { useCreateNewSpousalAppMutation } from 'graphql/mutations/customers/createNewSpousalApp/mutation';
import { START_FULL_PATH } from 'routes/constants';
import handleError from 'util/handleError';

import {
  BUTTON_ANALYTICS_PROPERTIES,
  HEADER,
  SOMEONE_ELSE,
  SUBHEADER,
  SELF_PLAYBACK,
  SOMEONE_ELSE_PLAYBACK,
} from './constants';
import styles from './select-user.module.scss';
import { IProps } from './types';
import { LOG_OUT_OPTION } from './utils';

const SelectUser: React.FC<IProps> = ({ activeUserUuid, firstName, onMyselfSubmission }) => {
  const { logout, isAuthenticated } = useAuth0();

  const createSpousalAppComplete = (): void => {
    createNewSpousalAppComplete(activeUserUuid, dispatch);

    // NOTE: If this path ever changes we'll need to update the Auth0
    // "Allowed logout URLS" with the new URL in each tenant.
    const currentPage = `${window.location.origin}${START_FULL_PATH}${window.location.search}`;

    if (isAuthenticated) {
      logout({ returnTo: currentPage });
    } else {
      window.location.assign(currentPage);
    }
  };

  const createSpousalAppOnError = (): void => {
    handleError(dispatch);
  };

  const [createSpousalApp] = useCreateNewSpousalAppMutation({
    onCompleted: createSpousalAppComplete,
    onError: createSpousalAppOnError,
  });

  const dispatch = useDispatch();

  const logOut = () => {
    openPage(LOG_OUT_OPTION.url, LOG_OUT_OPTION.target, LOG_OUT_OPTION.analytics);
  };

  const logOutCTA = (className: string) => {
    const text = firstName ? `Not ${firstName}?` : null;

    return (
      <UtilityText className={styles[className]} size="medium">
        {text}
        <Button
          analyticsProperties={LOG_OUT_OPTION.analytics}
          className={styles.logout}
          fullWidth={false}
          onClick={logOut}
          variant="secondary-no-fill"
          type="button"
        >
          <UtilityText>{LOG_OUT_OPTION.text}</UtilityText>
        </Button>
      </UtilityText>
    );
  };

  const selectUser = (selection: IMultipleChoiceOption): void => {
    trackSelectedOption(
      selection.value === activeUserUuid ? 'myself' : SOMEONE_ELSE,
      StepName.SELECT_USER
    );
    if (selection.value === SOMEONE_ELSE) {
      dispatch(startLoader());
      createSpousalApp({
        variables: {
          input: {
            customer_id: activeUserUuid,
            source_path: window.location.pathname,
          },
        },
      });
    } else {
      onMyselfSubmission();
    }
  };

  const label = (primary: boolean) => {
    const defaultText = "I'm shopping for ";

    let secondaryText: string;

    if (primary) {
      secondaryText = `myself${firstName ? ` (${firstName})` : ''}`;
    } else {
      secondaryText = 'someone else';
    }

    return (
      <UtilityText size="medium">
        {defaultText}
        <UtilityText as="strong" size="medium" weight="bold">
          {secondaryText}
        </UtilityText>
      </UtilityText>
    );
  };

  const primaryUserOpt: IMultipleChoiceOption = {
    label: label(true),
    playback: SELF_PLAYBACK,
    secondaryOption: logOutCTA('logout-desktop'),
    value: activeUserUuid,
  };

  const secondaryUserOpt: IMultipleChoiceOption = {
    label: label(false),
    playback: SOMEONE_ELSE_PLAYBACK,
    value: SOMEONE_ELSE,
  };

  const userOptions: IMultipleChoiceOption[] = [primaryUserOpt, secondaryUserOpt];

  return (
    <MultipleChoice
      analyticsProperties={BUTTON_ANALYTICS_PROPERTIES}
      defaultOption={userOptions[0]}
      header={HEADER}
      id="select-user"
      onSubmit={selectUser}
      options={userOptions}
      secondaryOption={logOutCTA('logout-mobile')}
      subheader={SUBHEADER}
    />
  );
};

export default SelectUser;
